import React from 'react';
import { MessageCircle, Mail, MapPin, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

export const ContactForm = () => {
  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Get In <span className="text-emerald-600">Touch</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connect with our investment team to explore opportunities and discuss how Credora Capital can help achieve your investment objectives
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Email Contact */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Email Us</h3>
                <div className="space-y-3">
                  <a 
                    href="mailto:gp@greenflow.ventures"
                    className="inline-flex items-center text-lg font-semibold text-emerald-600 hover:text-emerald-700 transition-colors duration-200"
                  >
                    gp@greenflow.ventures
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                  <p className="text-gray-600">
                    We'll respond within 24 hours
                  </p>
                  <Button
                    onClick={() => window.open('mailto:gp@greenflow.ventures?subject=Investment Inquiry - Credora Capital', '_blank')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white mt-4"
                  >
                    Send Email
                  </Button>
                </div>
              </div>
            </div>

            {/* WhatsApp Contact */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Chat with Us</h3>
                <div className="space-y-3">
                  <a 
                    href="https://wa.me/919455461460?text=Hello,%0A%0AI%20am%20interested%20in%20learning%20more%20about%20Credora%20Capital%20investment%20opportunities.%0A%0APlease%20assist%20with%20further%20details.%0A%0AThank%20you."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-lg font-semibold text-emerald-600 hover:text-emerald-700 transition-colors duration-200"
                  >
                    +91 9455461460
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                  <p className="text-gray-600">
                    WhatsApp available 24/7
                  </p>
                  <Button
                    onClick={() => window.open('https://wa.me/919455461460?text=Hello,%0A%0AI%20am%20interested%20in%20learning%20more%20about%20Credora%20Capital%20investment%20opportunities.%0A%0APlease%20assist%20with%20further%20details.%0A%0AThank%20you.', '_blank')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white mt-4"
                  >
                    Start Chat
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Office Information */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <MapPin className="w-6 h-6 text-emerald-600" />
                <h4 className="text-lg font-semibold text-gray-900">Office Location</h4>
              </div>
              <p className="text-gray-700 mb-2">Daryaganj, Delhi, India</p>
              <p className="text-sm text-gray-600">Regulated under SEBI</p>
              <p className="text-sm text-gray-600">Category I AIF</p>
            </div>

            {/* Investment Criteria */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Investment Criteria</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Minimum Investment: ₹1 Crore</li>
                <li>• Investor Type: QIBs and HNIs</li>
                <li>• Lock-in Period: 3 years</li>
                <li>• Fund Tenure: 7-8 years</li>
              </ul>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="text-center mt-12">
            <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Ready to Start Your Investment Journey?</h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Reach out to discuss investment opportunities in India's vibrant primary market ecosystem. 
                Our team is ready to answer your questions about our fund strategy and investment process.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <Button
                  onClick={() => window.open('mailto:gp@greenflow.ventures?subject=Investment Inquiry - Credora Capital&body=Hello,%0D%0A%0D%0AI am interested in learning more about Credora Capital investment opportunities.%0D%0A%0D%0APlease contact me to discuss further.%0D%0A%0D%0AThank you.', '_blank')}
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3"
                >
                  <Mail className="mr-2 w-5 h-5" />
                  Send Investment Inquiry
                </Button>
                <Button
                  onClick={() => window.open('https://wa.me/919455461460?text=Hello,%0A%0AI%20am%20interested%20in%20learning%20more%20about%20Credora%20Capital%20investment%20opportunities.%0A%0APlease%20assist%20with%20further%20details.%0A%0AThank%20you.', '_blank')}
                  variant="outline"
                  size="lg"
                  className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white px-8 py-3"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Chat on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};